import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Clock, ArrowRight, Search } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClientServicesPage() {
  const [locations, setLocations] = useState([]);
  const [selectedLocId, setSelectedLocId] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const locRes = await axios.get(`${API}/public/locations`);
        const locs = locRes.data || [];
        setLocations(locs);
        if (locs.length) setSelectedLocId(locs[0].location_id);
      } catch (err) {
        console.error("Failed to load public locations:", err);
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!selectedLocId) return;
    axios.get(`${API}/public/services?location_id=${selectedLocId}`).then(res => setServices(res.data || []));
  }, [selectedLocId]);

  const grouped = useMemo(() => {
    const filtered = q ? services.filter(s => s.name.toLowerCase().includes(q.toLowerCase())) : services;
    return filtered.reduce((acc, s) => {
      const cat = s.category || "Other";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(s);
      return acc;
    }, {});
  }, [services, q]);

  return (
    <div data-testid="client-services-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 via-white to-rose-50/40 border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
          <p className="text-xs font-bold uppercase tracking-widest text-rose-500 font-['Manrope'] mb-3">Service Menu</p>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 mb-3" data-testid="services-page-title">
            Everything we offer.
          </h1>
          <p className="text-lg text-slate-600 font-['Manrope'] max-w-2xl">
            Browse our full menu of hair, skin, nail, and beauty services. Transparent pricing and expert care, every visit.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-8">
          {locations.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="services-location-filter">
              {locations.map(l => (
                <button key={l.location_id} onClick={() => setSelectedLocId(l.location_id)}
                  data-testid={`loc-filter-${l.location_id}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium font-['Manrope'] transition-all ${
                    selectedLocId === l.location_id ? "bg-slate-900 text-white" : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                  }`}>
                  {l.name}
                </button>
              ))}
            </div>
          )}
          <div className="relative sm:ml-auto w-full sm:w-72">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search services"
              data-testid="services-search"
              className="w-full pl-10 pr-4 h-10 rounded-full border border-slate-200 text-sm focus:outline-none focus:border-slate-400 font-['Manrope']" />
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center text-sm text-slate-400 font-['Manrope']">Loading…</div>
        ) : Object.keys(grouped).length === 0 ? (
          <div className="py-20 text-center text-sm text-slate-400 font-['Manrope']">No services found.</div>
        ) : (
          <div className="space-y-12">
            {Object.entries(grouped).map(([cat, svcs]) => (
              <div key={cat}>
                <h2 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight mb-5">{cat}</h2>
                <div className="divide-y divide-slate-100 border border-slate-100 rounded-2xl bg-white overflow-hidden">
                  {svcs.map(s => (
                    <div key={s.service_id} className="p-5 sm:p-6 flex items-center justify-between gap-4 hover:bg-slate-50/50 transition-colors"
                      data-testid={`service-row-${s.service_id}`}>
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="w-1 h-14 rounded-full flex-shrink-0" style={{ backgroundColor: s.color || "#F43F5E" }} />
                        <div className="min-w-0">
                          <h3 className="font-['Outfit'] text-base sm:text-lg font-semibold text-slate-900 truncate">{s.name}</h3>
                          <div className="flex items-center gap-3 mt-1">
                            <div className="flex items-center gap-1 text-xs text-slate-500 font-['Manrope']">
                              <Clock className="w-3 h-3" />
                              {s.duration} min
                            </div>
                            {s.description && <p className="text-xs text-slate-500 font-['Manrope'] truncate hidden sm:block">{s.description}</p>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <p className="font-['Outfit'] text-xl font-semibold text-slate-900">${s.price}</p>
                        <Link to="/salon/book">
                          <Button size="sm" className="h-9 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-4 text-xs font-semibold" data-testid={`book-service-cta-${s.service_id}`}>
                            Book <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
